import React, { useState, useEffect } from 'react';
import axios from 'axios';
import showToast from '../../utils/toastHelper';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  // const [filter, setFilter] = useState({ action: '', userId: '' });

  useEffect(() => {
    fetchLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit]);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/audit-logs?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLogs(response.data.data.logs);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.pagination.total,
        totalPages: response.data.data.pagination.totalPages,
      }));
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      showToast('Failed to load audit logs', 'error');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionBadge = (action) => {
    const badges = {
      create: 'success',
      update: 'warning',
      delete: 'danger',
      login: 'info',
      logout: 'secondary',
    };
    return badges[action.toLowerCase()] || 'primary';
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="mb-4">
        <h3 className="mb-1" style={{ fontWeight: '600' }}>Audit Logs</h3>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          Track all system activities and user actions
        </p>
      </div>

      {/* Info Card */}
      <div className="card mb-4" style={{ borderLeft: '4px solid #3b82f6' }}>
        <div className="card-body">
          <div className="d-flex align-items-start">
            <i className="bi bi-info-circle me-3" style={{ fontSize: '1.5rem', color: '#3b82f6' }}></i>
            <div>
              <h6 style={{ fontWeight: '600', color: '#1e40af' }}>About Audit Logs</h6>
              <p className="mb-0" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Audit logs provide a detailed record of all system activities, including user actions, 
                data modifications, and administrative changes. This helps maintain accountability and security.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="card">
        <div className="card-body">
          {logs.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-file-text" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No audit logs found</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Entity Type</th>
                      <th>Details</th>
                      <th>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td style={{ fontSize: '0.875rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {formatDate(log.createdAt)}
                        </td>
                        <td>
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                            {log.user?.fullName || 'System'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {log.user?.email || '-'}
                          </div>
                        </td>
                        <td>
                          <span className={`badge bg-${getActionBadge(log.action)}`} style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                            {log.action}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.875rem' }}>
                          {log.entityType || '-'}
                        </td>
                        <td style={{ fontSize: '0.875rem', maxWidth: '300px' }}>
                          {log.details ? (
                            <div style={{ 
                              whiteSpace: 'nowrap', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis' 
                            }} title={log.details}>
                              {log.details}
                            </div>
                          ) : '-'}
                        </td>
                        <td style={{ fontSize: '0.875rem', fontFamily: 'monospace', color: '#6b7280' }}>
                          {log.ipAddress || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mt-4">
          <div className="d-flex align-items-center gap-3">
            <div className="text-muted" style={{ fontSize: '0.875rem' }}>
              Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} logs
            </div>
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="pageSize" className="text-muted mb-0" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Per page:</label>
              <select
                id="pageSize"
                className="form-select form-select-sm"
                value={pagination.limit}
                onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                style={{ width: 'auto' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {pagination.totalPages > 1 && (
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                const pageNum = pagination.page <= 3 ? i + 1 : pagination.page - 2 + i;
                if (pageNum > pagination.totalPages) return null;
                return (
                  <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
